import React, { useEffect, useCallback, useState } from 'react';
import { Form, Modal, Button, Steps, Row, Popconfirm, Skeleton } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import StepModalOne from './StepModalOne';
import StepModalTwo from './StepModalTwo';
import useQuotesContext from './QuotesContext';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';

const { Step } = Steps;

const ValidationQuoteModal = () => {
  const {
    visible,
    setVisible,
    setAnalyzeModalVisible,
    setFormModalValues,
    formModalValues,
    handleCancel,
    selectedQuote,
    visibleMailConfirm,
    setVisibleMailConfirm
  } = useQuotesContext();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const { dispatchAPI } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const { message } = useErrorMessage();
  const [quote, setQuote] = useState({});
  const [quittances, setQuittances] = useState();

  const generateQuittances = async (body) => {
    try {
      // eslint-disable-next-line no-underscore-dangle
      if (selectedQuote && body.effective_date._isValid) {
        const { data } = await dispatchAPI('POST', {
          url: `/contracts/generateQuittances/${selectedQuote?.key}`,
          body
        });
        setQuittances(data);
      } else {
        setQuittances(false);
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getQuote = useCallback(async () => {
    if (selectedQuote) {
      setIsLoading(true);
      try {
        const { data } = await dispatchAPI('GET', {
          url: `/contracts/${selectedQuote?.key}?populate=exemption,previous_contract,insurer,quittances`
        });
        setQuote({
          ...data,
          effective_date: data.effective_date
            ? moment(data.effective_date)
            : undefined
        });
        form.setFieldsValue({
          ...data,
          effective_date: data.effective_date
            ? moment(data.effective_date)
            : undefined
        });
      } catch (e) {
        if (e.response) message(e.response.status);
      }
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, [selectedQuote]);

  useEffect(() => {
    (async () => {
      await getQuote();
    })();
    // eslint-disable-next-line
  }, [getQuote]);

  const steps = [
    {
      title: quote.previous_contract
        ? t('validate_quote.form.summary_contract_amendment')
        : t('validate_quote.form.summary_contract'),
      content: <StepModalOne quote={quote} formModalValues={formModalValues} />
    },
    {
      title: t('validate_quote.form.banking_information'),
      content: (
        <StepModalTwo
          formModalValues={formModalValues}
          quote={quote}
          generateQuittances={generateQuittances}
          quittances={quittances}
          setFormModalValues={setFormModalValues}
        />
      )
    }
  ];

  const next = async () => {
    try {
      if (formModalValues.duty_of_advice === 'insurer') {
        setFormModalValues({
          ...formModalValues,
          offer_sought: null,
          compliant_offer: null,
          customer_profile: null
        });
      }
      await form.validateFields();
      if (current === steps.length - 1) {
        setVisible(false);
        setAnalyzeModalVisible(true);
      } else {
        setCurrent(current + 1);
      }
      document
        .getElementsByClassName('ant-modal-wrap ant-modal-centered')[0]
        .scrollTo({ behavior: 'smooth', top: 0 });
    } catch (e) {
      if (e.errorFields && e.errorFields.length)
        form.scrollToField(e.errorFields[0].name);
    }
  };

  const prev = () => {
    setCurrent(current - 1);
    document
      .getElementsByClassName('ant-modal-wrap ant-modal-centered')[0]
      .scrollTo({ behavior: 'smooth', top: 0 });
  };

  useEffect(() => {
    if (visible) {
      if (formModalValues) form.setFieldsValue(formModalValues);
      else form.resetFields();
    }
    // eslint-disable-next-line
  }, [visible]);

  const formLayout = {
    wrapperCol: { span: current === 0 ? 24 : 12 }
  };

  const handleChange = async (_, values) => {
    if (values.rib_type === 'INDIVIDUAL') {
      form.setFieldsValue({ rib_company_name: null });
      setFormModalValues({ ...formModalValues, rib_company_name: null });
    }
    if (values.rib_type === 'COMPANY') {
      form.setFieldsValue({
        rib_civility: null,
        rib_first_name: null,
        rib_last_name: null
      });
      setFormModalValues({
        ...formModalValues,
        rib_civility: null,
        rib_first_name: null,
        rib_last_name: null
      });
    }
    await setFormModalValues(values);
  };

  useEffect(() => {
    const datePickerDiv = document.getElementsByClassName('ant-picker-input');
    if (datePickerDiv.length) {
      const datePickerCollection =
        document.getElementsByClassName('ant-picker-input');
      if (datePickerCollection.length) {
        for (let i = 0; i < datePickerCollection.length; i += 1) {
          const [datePicker] =
            datePickerCollection[i].getElementsByTagName('INPUT');
          if (datePicker)
            datePicker.addEventListener('keyup', (e) => {
              if (e.which !== 8) {
                const numChars = datePicker.value.length;
                if (numChars === 2 || numChars === 5) {
                  let thisVal = datePicker.value;
                  thisVal += '/';
                  datePicker.value = thisVal;
                }
              }
            });
        }
      }
    }
  }, [current]);

  const handleFinish = () => {
    setVisible(false);
    setAnalyzeModalVisible(true);
  };

  return (
    <>
      <Modal
        title={
          quote?.previous_contract
            ? t('validate_quote.modal.amendment_contract')
            : t('validate_quote.modal.contract')
        }
        centered
        open={visible}
        footer={false}
        onCancel={() => handleCancel()}
        width={900}
      >
        <Steps current={current}>
          {steps.map((item) => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>
        <Skeleton loading={isLoading} paragraph={{ rows: 10 }}>
          <Form
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...formLayout}
            form={form}
            name="VALIDATEQUOTE"
            onFinish={handleFinish}
            layout="vertical"
            onValuesChange={handleChange}
            initialValues={{ rib_type: 'INDIVIDUAL' }}
          >
            {steps[current].content}
            <Row justify="center" style={{ marginTop: 40 }}>
              {current > 0 && (
                <Button
                  type="primary"
                  style={{ marginRight: '12px' }}
                  onClick={() => prev()}
                >
                  <LeftOutlined />
                  {`${t('buttons.prev')}`}
                </Button>
              )}
              {current < steps.length - 1 &&
                formModalValues &&
                formModalValues.compliant_offer !== false && (
                  <Button type="primary" onClick={() => next()}>
                    {`${t('buttons.next')}`}
                    <RightOutlined />
                  </Button>
                )}
              {current === steps.length - 1 && (
                <Popconfirm
                  open={visibleMailConfirm}
                  title={t('errors.message.email_authorization')}
                  onConfirm={() => {
                    form.setFieldsValue({
                      ...formModalValues,
                      email_authorization: true
                    });
                    setFormModalValues({
                      ...formModalValues,
                      email_authorization: true
                    });
                    setVisibleMailConfirm(false);
                  }}
                  onCancel={() => {
                    form.setFieldsValue({
                      ...formModalValues,
                      email_authorization: false
                    });
                    setFormModalValues({
                      ...formModalValues,
                      email_authorization: false
                    });
                    setVisibleMailConfirm(false);
                  }}
                  okText={t('validate_quote.form.radio_yes')}
                  cancelText={t('validate_quote.form.radio_no')}
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={
                      !formModalValues?.email_authorization ||
                      !formModalValues?.gdpr_consent ||
                      (quote?.type === 'RCGLS' &&
                        quote?.payment_type === 'DIRECTDEBIT' &&
                        !formModalValues.subscriber_iban_confirmation) ||
                      (quote?.type === 'RCNGAWK' &&
                        quittances?.[0]?.quittance_status === 'NOT_PAID')
                    }
                    onClick={() => {
                      if (formModalValues.email_authorization === undefined) {
                        setVisibleMailConfirm(true);
                      } else {
                        next();
                      }
                    }}
                  >
                    <CheckOutlined />
                    {!quote?.previous_contract
                      ? t('buttons.validateQuote')
                      : t('buttons.validateAmendment')}
                  </Button>
                </Popconfirm>
              )}
              <Button danger type="link" onClick={handleCancel}>
                {t('buttons.cancel')}
                <CloseOutlined />
              </Button>
            </Row>
          </Form>
        </Skeleton>
      </Modal>
    </>
  );
};

export default ValidationQuoteModal;
